/**
 * 全局 SEO Mixin
 * 根据当前路由路径自动返回对应页面的 meta 配置
 * 在 Layout.vue 中引入即可全站生效
 */
import { Vue, Component } from "vue-property-decorator";
import {
  generateMetaInfo,
  getServiceJsonLd,
  getOrganizationJsonLd,
  getWebSiteJsonLd,
  getCollectionPageJsonLd,
  getAboutPageJsonLd,
  getFaqJsonLd,
  getHowToJsonLd,
  PageSeoOptions
} from "@/utils/seo";

// 所有页面的 SEO 配置映射表（key 为路由路径）
const PAGE_SEO_CONFIG: Record<string, PageSeoOptions> = {
  "/": {
    title: "光华赋能 | 科学经营助推企业高质量发展（始于2003）",
    description:
      "光华赋能始于2003年，专注企业科学经营培训与咨询陪跑，提供BLM科学经营、实战EMBA、企业内训、管理微咨询、数字化绩效系统等服务，累计服务上万家企业，用科学经营助推企业高质量发展。",
    keywords:
      "企业培训,管理咨询,科学经营,BLM,EMBA,企业内训,绩效管理,管理微咨询,数字化转型,企业陪跑,光华赋能",
    path: "/",
    breadcrumb: [{ name: "首页" }],
    jsonLd: [
      getServiceJsonLd({
        name: "企业科学经营培训与咨询陪跑",
        description:
          "光华赋能提供BLM科学经营、实战EMBA、企业内训、管理微咨询、数字化绩效系统等企业培训与咨询服务，用科学经营助推企业高质量发展。",
        category: "企业培训与咨询"
      }),
      getFaqJsonLd([
        { question: "光华赋能是什么公司？", answer: "杭州光华赋能教育科技有限公司（原时代光华），由方永飞先生创立于2003年，以「用科学经营助推企业高质量发展」为使命，致力于为企业提供集培训、咨询、数字化于一体的综合赋能解决方案，是「专精特新」企业公共服务示范平台。" },
        { question: "光华赋能提供哪些核心业务？", answer: "光华构建了六位一体业务模式：光华大增长营、光华共创会（实战EMBA、大思维书院、常青藤新生代联盟）、光华科研院、光华数字化（绩效飞轮、微学、ELP云学习、光华商学）、光华商学院（公开课学习卡）、光华咨询陪跑，全面赋能企业高质量发展。" },
        { question: "什么是科学经营方法论？", answer: "科学经营是光华经过20余年专业企业经营研究总结的方法论，包含BLM科学经营方法论和分拆对工作法，帮助企业掌握从战略制定到落地执行的全流程方法，实现可持续的结构性增长。" },
        { question: "光华赋能服务过多少企业？", answer: "截至目前，光华累计服务20000+企业客户，实施100+企业陪跑计划，培训250000+面授学员，共建300+商学院，是国内领先的企业培训咨询陪跑机构。" },
        { question: "如何联系光华赋能？", answer: "光华赋能服务热线：400-0808-155。公司总部位于杭州，可通过官网在线咨询或拨打热线了解企业培训、咨询陪跑、数字化系统等服务详情。" }
      ])
    ]
  },
  "/about": {
    title: "关于光华 | 杭州光华赋能教育科技有限公司，始于2003年的企业培训咨询机构 - 光华赋能",
    description:
      "杭州光华赋能教育科技有限公司（原时代光华），始于2003年，专注企业科学经营培训与咨询陪跑20余年。提供BLM科学经营、实战EMBA、企业内训、管理微咨询、数字化绩效系统等服务，累计服务上万家企业。",
    keywords:
      "关于光华,光华赋能,杭州光华赋能,时代光华,企业培训公司,管理咨询机构,企业介绍,光华赋能官网",
    path: "/about",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "关于光华" }
    ],
    jsonLd: [
      getAboutPageJsonLd(),
      getFaqJsonLd([
        { question: "光华赋能是什么时候成立的？", answer: "光华赋能（原时代光华）始于2003年，专注企业科学经营培训与咨询陪跑20余年。" },
        { question: "光华赋能的主营业务是什么？", answer: "光华赋能提供BLM科学经营、实战EMBA、企业内训、管理微咨询、数字化绩效系统等企业培训与咨询服务。" },
        { question: "光华赋能服务过多少企业？", answer: "光华赋能累计服务上万家企业，覆盖制造业、服务业、互联网、零售等多个行业。" },
        { question: "光华赋能的总部在哪里？", answer: "光华赋能总部位于杭州，全称为杭州光华赋能教育科技有限公司。" }
      ])
    ]
  },
  "/co-creation-emba": {
    title: "共创会EMBA | 培养专业的老板 - 香港光华管理学院实战EMBA - 光华赋能",
    description:
      "香港光华管理学院共创会EMBA，面向中小企业创始人与董事长的实战型工商管理教育项目。以培养专业的老板为使命，提供科学经营1+1+1课程体系、实战派导师、十步学习流程、九大核心权益，18个月学期，入学即享共创会会员权益。",
    keywords:
      "共创会EMBA,实战EMBA,香港光华管理学院,企业老板培训,创始人培训,工商管理,EMBA总裁班,光华赋能",
    path: "/co-creation-emba",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "关于光华" },
      { name: "共创会EMBA" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "共创会EMBA",
        description:
          "香港光华管理学院与杭州光华赋能联合打造的实战型工商管理教育项目，面向中小企业创始人与董事长，18个月学期，科学经营1+1+1课程体系。",
        category: "EMBA总裁培训"
      }),
      getFaqJsonLd([
        { question: "共创会EMBA适合什么人参加？", answer: "共创会EMBA面向中小企业创始人与董事长，旨在培养专业的企业老板。" },
        { question: "共创会EMBA学期多长？", answer: "共创会EMBA学期为18个月，入学即享共创会会员权益。" },
        { question: "共创会EMBA的课程体系是什么？", answer: "采用科学经营1+1+1课程体系，结合实战派导师与十步学习流程。" },
        { question: "共创会EMBA颁发什么证书？", answer: "完成学业后颁发香港光华管理学院实战EMBA相关证书。" }
      ])
    ]
  },
  "/teacher": {
    title: "光华师资 | 上百名师大咖、行业专家组成的专业师资团队 - 光华赋能",
    description:
      "光华赋能拥有上百名师大咖、行业专家组成的庞大师资库，具备丰富的培训管理经验，涵盖战略、管理、营销、人力、财务、生产等领域，为企业提供专业的培训与咨询服务。",
    keywords:
      "光华师资,讲师团队,培训讲师,管理专家,行业大咖,企业培训师资,光华赋能",
    path: "/teacher",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "关于光华" },
      { name: "光华师资" }
    ],
    jsonLd: getCollectionPageJsonLd({
      name: "光华师资团队",
      description: "光华赋能拥有上百名师大咖、行业专家组成的庞大师资库，涵盖战略、管理、营销、人力、财务、生产等领域。",
      path: "/teacher"
    })
  },
  "/case": {
    title: "客户案例 | 企业成功案例与陪跑实践 - 光华赋能",
    description:
      "光华赋能客户成功案例，覆盖制造业、服务业、互联网、零售等多个行业，展示企业在科学经营、管理提升、数字化转型等方面的陪跑实践与成果。",
    keywords:
      "客户案例,成功案例,企业案例,管理咨询案例,培训案例,企业陪跑,光华赋能",
    path: "/case",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "关于光华" },
      { name: "客户案例" }
    ],
    jsonLd: getCollectionPageJsonLd({
      name: "客户成功案例",
      description: "光华赋能客户成功案例，覆盖制造业、服务业、互联网、零售等多个行业，展示企业陪跑实践与成果。",
      path: "/case"
    })
  },
  "/news": {
    title: "光华动态 | 光华活动资讯、大事记与行业洞察 - 光华赋能",
    description:
      "光华赋能动态，涵盖线下活动资讯、思想碰撞与共同成长、光华大事记。二十余年专业企业经营研究，高维发展、全面赋能、长期陪跑，分享企业经营管理洞察与实践。",
    keywords:
      "光华动态,光华资讯,企业活动,管理资讯,行业洞察,光华大事记,企业经营,光华赋能",
    path: "/news",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "关于光华" },
      { name: "光华动态" }
    ],
    jsonLd: getCollectionPageJsonLd({
      name: "光华动态资讯",
      description: "光华赋能动态，涵盖线下活动资讯、思想碰撞与共同成长、光华大事记，分享企业经营管理洞察与实践。",
      path: "/news"
    })
  },
  "/course": {
    title: "公开课 | 光华赋能公开课程列表，涵盖战略、管理、营销、人力等领域 - 光华赋能",
    description:
      "光华赋能公开课列表，涵盖企业战略、经营管理、市场营销、人力资源、财务管理、生产运营等领域，定期开课，帮助企业管理者系统提升经营管理能力。",
    keywords:
      "公开课,企业培训课程,管理课程,战略管理,营销课程,人力课程,公开课程,光华赋能",
    path: "/course",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "光华商学院" },
      { name: "公开课" }
    ],
    jsonLd: getCollectionPageJsonLd({
      name: "光华公开课列表",
      description: "光华赋能公开课列表，涵盖企业战略、经营管理、市场营销、人力资源、财务管理、生产运营等领域。",
      path: "/course"
    })
  },
  "/growth-camp": {
    title: "大增长营 | BLM科学经营方法论+工作法，可复制的结构性增长 - 光华赋能",
    description:
      "光华赋能大增长营，以BLM科学经营为核心，结合方法论+工作法，帮助企业实现可复制的结构性增长。面向企业创始人与核心高管，通过系统学习与实战演练，破解增长瓶颈。",
    keywords:
      "大增长营,BLM科学经营,企业增长,结构性增长,方法论,工作法,企业培训,光华赋能",
    path: "/growth-camp",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "BLM科学经营" },
      { name: "大增长营" }
    ],
    jsonLd: getServiceJsonLd({
      name: "大增长营",
      description:
        "以BLM科学经营为核心，结合方法论+工作法，帮助企业实现可复制的结构性增长的高管培训项目。",
      category: "BLM科学经营培训"
    })
  },
  "/research-institute": {
    title: "科研院 | 企业创始人与高管共同学习科学经营的实践社群 - 光华赋能",
    description:
      "光华科学经营研究院（科研院）是企业创始人与高管共同学习用科学经营实现高质量发展的实践社群。通过课堂共学、企业私董会、对标管理工具，帮助企业破解成长瓶颈。",
    keywords:
      "科研院,科学经营研究院,企业创始人培训,高管学习社群,BLM,BSC,企业对标管理,光华赋能",
    path: "/research-institute",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "BLM科学经营" },
      { name: "科研院" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "光华科学经营研究院",
        description:
          "企业创始人与高管共同学习用科学经营实现高质量发展的实践社群，提供课堂共学、私董会、对标管理等服务。",
        category: "高管学习社群"
      }),
      getFaqJsonLd([
        { question: "科研院是什么？", answer: "光华科学经营研究院是企业创始人与高管共同学习科学经营的实践社群。" },
        { question: "科研院提供哪些服务？", answer: "提供课堂共学、企业私董会、对标管理工具等学习与交流服务。" },
        { question: "科研院适合什么人参加？", answer: "适合企业创始人与核心高管，希望系统学习科学经营方法论的人群。" }
      ])
    ]
  },
  "/academy": {
    title: "大思维书院 | 终身学习·轻辅导·微咨询·核心资源 - 光华赋能",
    description:
      "光华共创会大思维书院，面向企业负责人提供终身学习、轻辅导、微咨询与核心资源对接服务。加入共创会圈层，学东西、交朋友、整资源、增业务。",
    keywords:
      "大思维书院,共创会,终身学习,企业辅导,微咨询,资源对接,企业圈层,光华赋能",
    path: "/academy",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "共创会" },
      { name: "大思维书院" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "大思维书院",
        description:
          "面向企业负责人的终身学习、轻辅导、微咨询与核心资源对接服务平台。",
        category: "企业学习圈层"
      }),
      getFaqJsonLd([
        { question: "大思维书院提供什么服务？", answer: "提供终身学习、轻辅导、微咨询与核心资源对接服务。" },
        { question: "加入大思维书院有什么好处？", answer: "学东西、交朋友、整资源、增业务，融入共创会高端圈层。" },
        { question: "大思维书院适合什么人？", answer: "适合企业负责人，希望持续学习并对接优质资源的人群。" }
      ])
    ]
  },
  "/emba": {
    title: "实战EMBA总裁班 | 光华共创会×香港光华管理学院 - 光华赋能",
    description:
      "光华共创会与香港光华管理学院联合发起实战EMBA总裁班，旨在培养专业的企业老板，做中国最落地、最接地气的实战EMBA。面向企业负责人、联合创始人，系统学习经营管理实战课程。",
    keywords:
      "实战EMBA,EMBA总裁班,光华共创会,香港光华管理学院,企业老板培训,创始人培训,经营管理,光华赋能",
    path: "/emba",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "共创会" },
      { name: "实战EMBA" }
    ],
    jsonLd: getServiceJsonLd({
      name: "实战EMBA总裁班",
      description:
        "光华共创会与香港光华管理学院联合发起的实战EMBA总裁班，面向企业负责人与联合创始人，提供落地的经营管理实战课程。",
      category: "EMBA总裁培训"
    })
  },
  "/ivy": {
    title: "常青藤 | 企业接班人·新商业领袖培养计划 - 光华赋能",
    description:
      "光华共创会常青藤项目，面向企业接班人与新商业领袖，聚焦企业传承、第二增长曲线、传统转型、代际对话，提供专属活动与国际视野，助力企业永续发展。",
    keywords:
      "常青藤,企业接班人,新商业领袖,企业传承,第二增长曲线,传统转型,代际对话,光华赋能",
    path: "/ivy",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "共创会" },
      { name: "常青藤" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "常青藤",
        description:
          "面向企业接班人与新商业领袖的培养计划，聚焦企业传承、第二增长曲线与代际对话。",
        category: "企业接班人培养"
      }),
      getFaqJsonLd([
        { question: "常青藤项目面向什么人群？", answer: "面向企业接班人与新商业领袖，聚焦企业传承与创新发展。" },
        { question: "常青藤项目有哪些特色？", answer: "聚焦企业传承、第二增长曲线、传统转型、代际对话，提供专属活动与国际视野。" }
      ])
    ]
  },
  "/business-school": {
    title: "光华商学院 | 实战·实操·实用·实效·实践，助推企业高质量发展 - 光华赋能",
    description:
      "光华商学院追求实战、实操、实用、实效、实践，帮助企业实现少躺坑、少走弯路、少花冤枉钱。提供企业内训、公开课、光华研修班等系统化培训服务，用科学经营助推企业高质量发展。",
    keywords:
      "光华商学院,企业培训,实战培训,企业内训,公开课,研修班,管理培训,光华赋能",
    path: "/business-school",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "光华商学院" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "光华商学院",
        description:
          "提供企业内训、公开课、研修班等系统化企业培训服务，追求实战、实操、实用、实效、实践。",
        category: "企业商学院培训"
      }),
      getFaqJsonLd([
        { question: "光华商学院有哪些培训服务？", answer: "提供企业内训、公开课、光华研修班等系统化培训服务。" },
        { question: "光华商学院的培训理念是什么？", answer: "追求实战、实操、实用、实效、实践，帮助企业少走弯路。" },
        { question: "光华商学院课程覆盖哪些领域？", answer: "涵盖战略、管理、营销、人力、财务、生产等企业经营全领域。" }
      ])
    ]
  },
  "/internal-training": {
    title: "企业内训 | 按需定制的企业内部培训与学习项目 - 光华赋能",
    description:
      "光华商学院企业内训服务，针对企业个性化、高效化的内部培训需求，凭借多年企业培训领域深耕经验与专业积淀，推出按需定制的企业内部培训服务，涵盖战略、管理、营销、人力等领域。",
    keywords:
      "企业内训,内部培训,定制培训,学习项目,企业培训方案,管理培训,光华赋能",
    path: "/internal-training",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "光华商学院" },
      { name: "企业内训" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "企业内训/学习项目",
        description:
          "针对企业个性化需求的定制化内部培训服务，涵盖战略、管理、营销、人力等领域。",
        category: "企业内训服务"
      }),
      getFaqJsonLd([
        { question: "企业内训可以定制吗？", answer: "可以，光华企业内训根据企业个性化需求定制培训内容与形式。" },
        { question: "企业内训覆盖哪些领域？", answer: "涵盖企业战略、经营管理、市场营销、人力资源、财务管理、生产运营等领域。" },
        { question: "企业内训的优势是什么？", answer: "按需定制、针对性强、性价比高，可结合企业实际案例开展教学。" }
      ])
    ]
  },
  "/mba": {
    title: "光华研修班 | CHO班·EMBA班·PMP班·CGO班·新媒体营销师班等八大研修班 - 光华赋能",
    description:
      "光华研修班涵盖八大研修班：首席人力官(CHO)班、职业经理人EMBA班、PMP项目经理班、首席增长官(CGO)班、新媒体营销师班、企业精益生产认证班、新晋管理者班、财税合规师班，系统提升管理能力。",
    keywords:
      "光华研修班,CHO班,EMBA班,PMP班,CGO班,新媒体营销,精益生产,新晋管理者,财税合规,管理研修班,光华赋能",
    path: "/mba",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "光华商学院" },
      { name: "光华研修班" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "光华研修班",
        description:
          "涵盖CHO、EMBA、PMP、CGO、新媒体营销、精益生产、新晋管理者、财税合规等八大研修班的系统管理培训。",
        category: "管理研修班培训"
      }),
      getFaqJsonLd([
        { question: "光华研修班有哪些班次？", answer: "涵盖CHO班、EMBA班、PMP班、CGO班、新媒体营销师班、精益生产认证班、新晋管理者班、财税合规师班八大研修班。" },
        { question: "光华研修班适合什么人？", answer: "适合企业中高层管理者，希望系统提升某一领域专业管理能力的人群。" }
      ])
    ]
  },
  "/accompany": {
    title: "咨询陪跑 | 管理微咨询·科学经营·人才·管理·增长陪跑 - 光华赋能",
    description:
      "光华赋能20年专注企业管理与培训，提供管理微咨询、科学经营陪跑、人才陪跑、管理陪跑、增长陪跑服务。周期短、见效快、费用低，覆盖企业战略、商业模式、股权设计、组织优化、绩效管理、薪酬体系、企业文化等领域。",
    keywords:
      "咨询陪跑,管理微咨询,企业咨询,战略咨询,股权设计,组织优化,绩效管理,薪酬体系,企业文化,企业陪跑,光华赋能",
    path: "/accompany",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "咨询陪跑" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "企业咨询陪跑",
        description:
          "提供管理微咨询、科学经营陪跑、人才陪跑、管理陪跑、增长陪跑服务，覆盖战略、股权、组织、绩效、薪酬、文化等领域。",
        category: "企业管理咨询"
      }),
      getHowToJsonLd({
        name: "光华赋能咨询陪跑服务流程",
        description: "光华赋能企业咨询陪跑的标准化服务流程",
        steps: [
          { name: "需求诊断", text: "深入企业调研，明确企业痛点与陪跑需求，制定个性化陪跑方案。" },
          { name: "方案设计", text: "基于诊断结果，设计科学经营、人才、管理或增长方向的陪跑方案。" },
          { name: "落地执行", text: "专家团队入驻陪跑，指导方案落地执行，解决实际问题。" },
          { name: "复盘优化", text: "定期复盘陪跑效果，优化调整方案，确保目标达成。" }
        ]
      })
    ]
  },
  "/digital-running-companion": {
    title: "数字化方案陪跑 | BLM绩效飞轮®+专家落地指导 - 光华赋能",
    description:
      "光华数字化方案陪跑，以BLM绩效飞轮®为基础，辅以专家落地指导，帮助企业完成数字化转型，实现绩效管理、组织协同与业务增长的数字化升级。",
    keywords:
      "数字化陪跑,数字化转型,BLM绩效飞轮,企业数字化,绩效系统,专家指导,光华赋能",
    path: "/digital-running-companion",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "数字化" },
      { name: "数字化方案陪跑" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "数字化方案陪跑",
        description:
          "以BLM绩效飞轮为基础，辅以专家落地指导的企业数字化转型陪跑服务。",
        category: "数字化转型咨询"
      }),
      getFaqJsonLd([
        { question: "数字化方案陪跑是什么？", answer: "以BLM绩效飞轮为基础，辅以专家落地指导，帮助企业完成数字化转型。" },
        { question: "数字化陪跑包含哪些内容？", answer: "包含绩效管理数字化、组织协同数字化、业务增长数字化等升级指导。" },
        { question: "数字化陪跑周期多长？", answer: "根据企业规模和需求定制，通常包含系统部署与专家落地指导阶段。" }
      ])
    ]
  },
  "/epm": {
    title: "EPM企业绩效系统 | 以绩效飞轮®为底座的企业绩效管理平台 - 光华赋能",
    description:
      "光华EPM企业绩效系统，以绩效飞轮®为底座，涵盖绩效管理、基础人事、主线作业流程、经营性财务、KM与ELN、CRM销售管理六大模块，帮助企业实现数字化绩效管理。",
    keywords:
      "EPM,企业绩效系统,绩效管理系统,绩效飞轮,人事管理,CRM,企业数字化,光华赋能",
    path: "/epm",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "数字化" },
      { name: "EPM企业绩效系统" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "EPM企业绩效系统",
        description:
          "以绩效飞轮为底座的企业绩效管理平台，涵盖绩效管理、人事、流程、财务、KM、CRM等模块。",
        category: "企业绩效管理软件"
      }),
      getFaqJsonLd([
        { question: "EPM企业绩效系统是什么？", answer: "以绩效飞轮为底座的企业绩效管理平台，帮助企业实现数字化绩效管理。" },
        { question: "EPM包含哪些模块？", answer: "涵盖绩效管理、基础人事、主线作业流程、经营性财务、KM与ELN、CRM销售管理六大模块。" },
        { question: "EPM适合什么企业？", answer: "适合希望通过数字化手段提升绩效管理水平的中大型企业。" }
      ])
    ]
  },
  "/performance-flywheel": {
    title: "绩效飞轮® | 让战略成为每个人的日常工作 - 光华赋能",
    description:
      "光华绩效飞轮®是一套让战略落地的绩效管理方法论与工具，通过战略解码、目标对齐、过程管理、绩效复盘的闭环，让战略成为每个人的日常工作，驱动企业持续增长。",
    keywords:
      "绩效飞轮,绩效管理,战略落地,战略解码,目标管理,OKR,KPI,绩效复盘,光华赋能",
    path: "/performance-flywheel",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "数字化" },
      { name: "绩效飞轮" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "绩效飞轮",
        description:
          "一套让战略落地的绩效管理方法论与工具，通过战略解码、目标对齐、过程管理、绩效复盘的闭环驱动企业增长。",
        category: "绩效管理方法论"
      }),
      getHowToJsonLd({
        name: "绩效飞轮落地四步法",
        description: "光华绩效飞轮让战略成为每个人日常工作的标准化流程",
        steps: [
          { name: "战略解码", text: "将企业战略拆解为可执行的目标与关键任务，明确战略方向。" },
          { name: "目标对齐", text: "自上而下对齐组织目标、部门目标与个人目标，确保力出一孔。" },
          { name: "过程管理", text: "建立日常过程管理机制，及时跟踪进度、解决问题、调整方向。" },
          { name: "绩效复盘", text: "定期复盘绩效结果，总结经验教训，持续优化提升。" }
        ]
      })
    ]
  },
  "/elp": {
    title: "ELP云学习生态平台 | 大企业在线学习平台，助力建立企业商学院 - 光华赋能",
    description:
      "光华ELP云学习生态平台，面向大企业的在线学习平台，助力企业建立自己的企业商学院。提供课程管理、学习运营、数据反馈、知识沉淀等一体化企业培训数字化解决方案。",
    keywords:
      "ELP,企业在线学习平台,企业商学院,学习平台,培训数字化,企业大学,在线教育,光华赋能",
    path: "/elp",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "数字化" },
      { name: "ELP云学习生态平台" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "ELP云学习生态平台",
        description:
          "面向大企业的在线学习平台，助力企业建立自己的企业商学院，提供课程管理、学习运营、数据反馈等一体化解决方案。",
        category: "企业在线学习平台"
      }),
      getFaqJsonLd([
        { question: "ELP云学习平台是什么？", answer: "面向大企业的在线学习平台，助力企业建立自己的企业商学院。" },
        { question: "ELP有哪些核心功能？", answer: "提供课程管理、学习运营、数据反馈、知识沉淀等一体化企业培训数字化解决方案。" },
        { question: "ELP适合什么规模的企业？", answer: "适合中大型企业，需要建立系统化企业培训数字化体系的组织。" }
      ])
    ]
  },
  "/weixue": {
    title: "微学 | 学·考·练·用，企业培训数字化管理平台 - 光华赋能",
    description:
      "光华微学是企业培训数字化管理平台，涵盖学、考、练、用全流程，提供学习运营与管理、海量课程库、数据化反馈、内部知识沉淀，帮助企业实现培训数字化转型。",
    keywords:
      "微学,企业培训平台,培训数字化,学习管理系统,LMS,在线培训,企业学习,知识沉淀,光华赋能",
    path: "/weixue",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "数字化" },
      { name: "微学" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "微学",
        description:
          "企业培训数字化管理平台，涵盖学、考、练、用全流程，提供学习运营、课程库、数据反馈、知识沉淀等功能。",
        category: "企业培训管理平台"
      }),
      getFaqJsonLd([
        { question: "微学是什么平台？", answer: "微学是企业培训数字化管理平台，涵盖学、考、练、用全流程。" },
        { question: "微学有哪些功能？", answer: "提供学习运营与管理、海量课程库、数据化反馈、内部知识沉淀等功能。" },
        { question: "微学支持移动端吗？", answer: "支持，微学是移动学习平台，可随时随地开展企业培训。" }
      ])
    ]
  },
  "/guanghua": {
    title: "光华商学 | 个人及企业学习小组移动学习平台，九大学堂350+门课程 - 光华赋能",
    description:
      "光华商学是个人及企业学习小组的移动学习平台，拥有九大学堂、350+门课程、4700+小节，满足各种个人及企业学习小组的学习需求，随时随地提升管理能力。",
    keywords:
      "光华商学,移动学习平台,在线课程,管理课程,企业学习,个人学习,九大学堂,知识付费,光华赋能",
    path: "/guanghua",
    breadcrumb: [
      { name: "首页", url: "/" },
      { name: "数字化" },
      { name: "光华商学" }
    ],
    jsonLd: [
      getServiceJsonLd({
        name: "光华商学",
        description:
          "个人及企业学习小组的移动学习平台，拥有九大学堂、350+门课程、4700+小节。",
        category: "移动学习平台"
      }),
      getFaqJsonLd([
        { question: "光华商学是什么？", answer: "光华商学是个人及企业学习小组的移动学习平台。" },
        { question: "光华商学有多少课程？", answer: "拥有九大学堂、350+门课程、4700+小节，满足各种学习需求。" },
        { question: "光华商学适合谁使用？", answer: "适合个人学习者及企业学习小组，随时随地提升管理能力。" }
      ])
    ]
  }
};

@Component({
  metaInfo(this: SeoMixin) {
    const path = this.$route.path;
    const matchedConfig = PAGE_SEO_CONFIG[path];
    // 未匹配到配置时（详情页、404等动态/未知路径），使用默认配置但 path 为当前路由路径，
    // 确保 canonical 指向当前页面而非首页
    const config = matchedConfig || {
      ...PAGE_SEO_CONFIG["/"],
      title: "光华赋能 | 科学经营助推企业高质量发展",
      path: path
    };
    const pageMeta = generateMetaInfo(config);
    return {
      ...pageMeta,
      htmlAttrs: { lang: "zh-CN" },
      script: [
        ...pageMeta.script,
        {
          vmid: "jsonld-organization",
          type: "application/ld+json",
          innerHTML: JSON.stringify(getOrganizationJsonLd())
        },
        {
          vmid: "jsonld-website",
          type: "application/ld+json",
          innerHTML: JSON.stringify(getWebSiteJsonLd())
        }
      ],
      __dangerouslyDisableSanitizersByTagID: {
        ...pageMeta.__dangerouslyDisableSanitizersByTagID,
        "jsonld-organization": ["innerHTML"],
        "jsonld-website": ["innerHTML"]
      }
    };
  }
})
export default class SeoMixin extends Vue {}
